import { BrowserCacheLocation, Configuration, LogLevel, RedirectRequest } from '@azure/msal-browser';

const defaultTenantId = '34f5f49b-7c38-409a-a524-0f645b90a5cf';

const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';

export interface AuthRuntimeConfig {
    azureTenantId?: string;
    azureClientId?: string;
}

interface AuthRuntimeConfigByHostname {
    _comment?: string;
    default?: AuthRuntimeConfig;
    [hostname: string]: AuthRuntimeConfig | string | undefined;
}

const AUTH_CONFIG_URL = '/auth-config.json';

function isAuthRuntimeConfig(config: unknown): config is AuthRuntimeConfig {
    return typeof config === 'object'
        && config !== null
        && ('azureTenantId' in config || 'azureClientId' in config);
}

export function resolveAuthRuntimeConfig(config: AuthRuntimeConfig | AuthRuntimeConfigByHostname, hostname: string): AuthRuntimeConfig {
    if (isAuthRuntimeConfig(config)) {
        return config;
    }

    const hostnameConfig = config[hostname];
    if (isAuthRuntimeConfig(hostnameConfig)) {
        return hostnameConfig;
    }

    if (isAuthRuntimeConfig(config.default)) {
        return config.default;
    }

    return {};
}

export async function loadAuthRuntimeConfig(): Promise<AuthRuntimeConfig> {
    const response = await fetch(AUTH_CONFIG_URL, { cache: 'no-store' });

    if (!response.ok) {
        throw new Error(`Could not load auth configuration: ${response.status}`);
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return resolveAuthRuntimeConfig(await response.json(), hostname);
}

export function getAuthConfigurationError(config: AuthRuntimeConfig): string | undefined {
    if (!config.azureClientId) {
        return 'Missing azureClientId in /auth-config.json. Configure the Entra application client ID.';
    }

    return undefined;
}

export function createMsalConfig(config: AuthRuntimeConfig): Configuration {
    const tenantId = config.azureTenantId || defaultTenantId;

    return {
        auth: {
            clientId: config.azureClientId || '',
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: `${appOrigin}/`,
            postLogoutRedirectUri: `${appOrigin}/`,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
        },
        system: {
            loggerOptions: {
                loggerCallback: (_level, message, containsPii) => {
                    if (!containsPii) {
                        console.debug(message);
                    }
                },
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    };
}

export const loginRequest: RedirectRequest = {
    scopes: ['openid', 'profile', 'email'],
};
